import {
  AMAP_CHARGING_VEHICLE_START,
  formatStationDistance,
  type GeoPoint
} from "./amapChargingDemo";
import { getApproxDistanceM } from "./amapParcelDemo";

export type FuelStationSource = "loading" | "amap" | "demo";

export interface FuelStation {
  id: string;
  name: string;
  shortName: string;
  address: string;
  brandLabel: string;
  location: GeoPoint;
  distanceM: number;
  gradeLabel: string;
  pricePerLiter: number;
  couponLabel: string;
  queueMinutes: number;
  routeMinutes: number;
  routeDistanceKm: number;
  detourKm: number;
  amountLabel: string;
  tag?: string;
  source: Exclude<FuelStationSource, "loading">;
}

const FALLBACK_FUEL_TEMPLATES = [
  {
    id: "sinopec-riverside",
    name: "中石化滨江加油站",
    shortName: "中石化滨江站",
    address: "当前路线东侧主路旁",
    brandLabel: "中石化",
    offsetLng: 0.0062,
    offsetLat: 0.0008,
    gradeLabel: "92#",
    pricePerLiter: 7.88,
    couponLabel: "支付宝满200减15",
    queueMinutes: 4,
    detourKm: 0.6,
    amountLabel: "200元",
    tag: "推荐"
  },
  {
    id: "petrochina-yunjin",
    name: "中石油云锦路站",
    shortName: "中石油云锦站",
    address: "云锦路北侧",
    brandLabel: "中石油",
    offsetLng: -0.0034,
    offsetLat: 0.0038,
    gradeLabel: "92#",
    pricePerLiter: 7.92,
    couponLabel: "会员价",
    queueMinutes: 7,
    detourKm: 0.9,
    amountLabel: "200元",
    tag: undefined
  },
  {
    id: "shell-quick-stop",
    name: "壳牌城市快捷站",
    shortName: "壳牌快捷站",
    address: "回家路线西南侧",
    brandLabel: "壳牌",
    offsetLng: 0.0012,
    offsetLat: -0.0044,
    gradeLabel: "95#",
    pricePerLiter: 8.42,
    couponLabel: "洗车券可叠加",
    queueMinutes: 3,
    detourKm: 1.1,
    amountLabel: "300元",
    tag: undefined
  }
] as const;

export function createFallbackFuelStations(
  vehicleLocation: GeoPoint = AMAP_CHARGING_VEHICLE_START.location
): FuelStation[] {
  return rankFuelStations(
    FALLBACK_FUEL_TEMPLATES.map((template) => {
      const location = {
        lng: vehicleLocation.lng + template.offsetLng,
        lat: vehicleLocation.lat + template.offsetLat
      };
      const distanceM = getApproxDistanceM(vehicleLocation, location);
      const routeDistanceKm = Math.max(0.7, distanceM / 1000 * 1.18);
      const routeMinutes = estimateFuelRouteMinutes(routeDistanceKm);

      return {
        id: template.id,
        name: template.name,
        shortName: template.shortName,
        address: template.address,
        brandLabel: template.brandLabel,
        location,
        distanceM,
        gradeLabel: template.gradeLabel,
        pricePerLiter: template.pricePerLiter,
        couponLabel: template.couponLabel,
        queueMinutes: template.queueMinutes,
        routeMinutes,
        routeDistanceKm,
        detourKm: template.detourKm,
        amountLabel: template.amountLabel,
        tag: template.tag,
        source: "demo" as const
      };
    })
  );
}

export function formatFuelDistance(distanceM: number) {
  return formatStationDistance(distanceM);
}

export function formatFuelPrice(pricePerLiter: number) {
  return `${pricePerLiter.toFixed(2).replace(/0$/, "")}元/L`;
}

export function getFuelStationDisplayName(station: FuelStation) {
  return station.shortName || station.name;
}

export function getRecommendedFuelStation(stations: FuelStation[]) {
  return stations.find((station) => station.tag === "推荐") ?? stations[0] ?? createFallbackFuelStations()[0];
}

export function rankFuelStations(stations: FuelStation[]) {
  return [...stations]
    .sort((a, b) => getFuelStationScore(b) - getFuelStationScore(a) || a.distanceM - b.distanceM)
    .map((station, index) => ({
      ...station,
      tag: index === 0 ? "推荐" : undefined
    }));
}

export function estimateFuelRouteMinutes(routeDistanceKm: number) {
  return Math.max(5, Math.round(routeDistanceKm / 22 * 60));
}

function getFuelStationScore(station: FuelStation) {
  const distanceScore = Math.max(0, 2600 - station.distanceM) / 2600 * 38;
  const queueScore = Math.max(0, 14 - station.queueMinutes) * 2.6;
  const couponScore = /减|券|会员|优惠|叠加/.test(station.couponLabel) ? 18 : 8;
  const detourScore = Math.max(0, 1.8 - station.detourKm) * 12;
  const priceScore = Math.max(0, 8.4 - station.pricePerLiter) * 10;

  return distanceScore + queueScore + couponScore + detourScore + priceScore;
}
